export type FaqItem = { q: string; a: string };

export type ToolContent = {
  intro: string;
  steps: string[];
  faq: FaqItem[];
};

export type Lang = "en" | "ro";

export const toolContentHeadings: Record<
  Lang,
  { howTo: string; faq: string; about: string }
> = {
  en: { howTo: "How to use it", faq: "Frequently asked questions", about: "About this tool" },
  ro: { howTo: "Cum se folosește", faq: "Întrebări frecvente", about: "Despre acest instrument" },
};

export const toolContent: Record<string, Record<Lang, ToolContent>> = {
  "password-generator": {
    en: {
      intro:
        "Create strong, random passwords with the length and character types you choose. Passwords are generated in your browser with the Web Crypto API, so they are never sent to a server.",
      steps: [
        "Set the password length with the slider.",
        "Pick the character types: uppercase, lowercase, numbers and symbols.",
        "Optionally exclude look-alike characters (i, l, 1, L, o, 0, O) or ambiguous symbols.",
        "Click Generate Password, then copy it with one click.",
      ],
      faq: [
        {
          q: "Is this password generator safe to use?",
          a: "Yes. Passwords are created locally using your browser's cryptographically secure random number generator and are never uploaded or stored on a server.",
        },
        {
          q: "How long should a password be?",
          a: "Use at least 12 characters, and 16 or more for important accounts. Longer passwords with a mix of character types are much harder to guess or brute-force.",
        },
        {
          q: "Does it save the passwords I generate?",
          a: "Only if you turn on the history option. History is kept in your browser's local storage on your own device and you can clear it at any time.",
        },
      ],
    },
    ro: {
      intro:
        "Creează parole puternice și aleatorii, cu lungimea și tipurile de caractere alese de tine. Parolele sunt generate în browser cu Web Crypto API, deci nu sunt trimise niciodată pe un server.",
      steps: [
        "Setează lungimea parolei cu glisorul.",
        "Alege tipurile de caractere: majuscule, minuscule, cifre și simboluri.",
        "Opțional, exclude caracterele care seamănă (i, l, 1, L, o, 0, O) sau simbolurile ambigue.",
        "Apasă Generează parola, apoi copiaz-o dintr-un click.",
      ],
      faq: [
        {
          q: "Este sigur acest generator de parole?",
          a: "Da. Parolele sunt create local, cu generatorul de numere aleatorii securizat criptografic al browserului, și nu sunt încărcate sau salvate pe un server.",
        },
        {
          q: "Cât de lungă trebuie să fie o parolă?",
          a: "Folosește cel puțin 12 caractere, iar pentru conturile importante 16 sau mai multe. Parolele lungi, cu tipuri diferite de caractere, sunt mult mai greu de ghicit.",
        },
        {
          q: "Salvează parolele generate?",
          a: "Doar dacă activezi opțiunea de istoric. Istoricul rămâne în stocarea locală a browserului, pe dispozitivul tău, și îl poți șterge oricând.",
        },
      ],
    },
  },

  "qr-code-generator": {
    en: {
      intro:
        "Turn any URL or text into a QR code you can download, and scan existing QR codes with your camera. Everything runs in your browser, so your content is not uploaded.",
      steps: [
        "Enter a URL or any text in the input field.",
        "Click Generate QR Code to create the image.",
        "Download the QR code, or copy a share link to it.",
        "To read a code instead, open the Scan tab and allow camera access.",
      ],
      faq: [
        {
          q: "Is the QR code generator free?",
          a: "Yes. It is free, needs no account and adds no watermark to the codes you create.",
        },
        {
          q: "What can I put in a QR code?",
          a: "Any text, including website links, contact details or short notes. Shorter content creates simpler codes that scan more reliably.",
        },
        {
          q: "Why does the scanner need camera access?",
          a: "Scanning uses your device camera to read the code. The video stays in your browser and is not recorded or uploaded.",
        },
      ],
    },
    ro: {
      intro:
        "Transformă orice URL sau text într-un cod QR pe care îl poți descărca și scanează coduri QR existente cu camera. Totul rulează în browser, deci conținutul tău nu este încărcat nicăieri.",
      steps: [
        "Introdu un URL sau orice text în câmpul de intrare.",
        "Apasă Generează cod QR pentru a crea imaginea.",
        "Descarcă codul QR sau copiază un link de partajare către el.",
        "Ca să citești un cod, deschide fila Scanează și permite accesul la cameră.",
      ],
      faq: [
        {
          q: "Este generatorul de coduri QR gratuit?",
          a: "Da. Este gratuit, nu necesită cont și nu adaugă filigran codurilor create.",
        },
        {
          q: "Ce pot pune într-un cod QR?",
          a: "Orice text: linkuri către site-uri, date de contact sau note scurte. Conținutul mai scurt produce coduri mai simple, care se scanează mai ușor.",
        },
        {
          q: "De ce are nevoie scannerul de acces la cameră?",
          a: "Scanarea folosește camera dispozitivului pentru a citi codul. Imaginea rămâne în browser și nu este înregistrată sau încărcată.",
        },
      ],
    },
  },

  "business-name-generator": {
    en: {
      intro:
        "Get brand name ideas from your own keywords, an industry, a tone and a naming format. Use it to brainstorm company, shop, app or project names, then check the ones you like for availability.",
      steps: [
        "Enter a few keywords that describe your business, separated by commas.",
        "Choose an industry, a tone and a name format.",
        "Adjust options such as alliteration, brand suffixes and how many names you want.",
        "Click Generate Names, then copy your favorites or all of them at once.",
      ],
      faq: [
        {
          q: "Are the generated names available as domains or trademarks?",
          a: "No. The tool only suggests ideas. Always check domain availability and search trademark databases before you commit to a name.",
        },
        {
          q: "How can I get better suggestions?",
          a: "Use specific keywords, try different tones and formats, and generate several batches. Combining results from a few runs often gives the best names.",
        },
        {
          q: "Do I need an account?",
          a: "No. Names are generated in your browser and nothing is stored on a server.",
        },
      ],
    },
    ro: {
      intro:
        "Primește idei de nume de brand pornind de la cuvintele tale cheie, un domeniu, un ton și un format. Folosește-l pentru a găsi nume de companie, magazin, aplicație sau proiect, apoi verifică disponibilitatea celor preferate.",
      steps: [
        "Introdu câteva cuvinte cheie care descriu afacerea, separate prin virgulă.",
        "Alege un domeniu, un ton și un format de nume.",
        "Ajustează opțiunile precum aliterația, sufixele de brand și numărul de nume dorite.",
        "Apasă Generează nume, apoi copiază numele preferate sau pe toate odată.",
      ],
      faq: [
        {
          q: "Numele generate sunt disponibile ca domenii sau mărci?",
          a: "Nu. Instrumentul doar sugerează idei. Verifică întotdeauna disponibilitatea domeniului și caută în bazele de date de mărci înainte de a te decide.",
        },
        {
          q: "Cum obțin sugestii mai bune?",
          a: "Folosește cuvinte cheie specifice, încearcă tonuri și formate diferite și generează mai multe serii. Combinarea rezultatelor din câteva încercări dă adesea cele mai bune nume.",
        },
        {
          q: "Am nevoie de cont?",
          a: "Nu. Numele sunt generate în browser și nimic nu este salvat pe un server.",
        },
      ],
    },
  },

  "free-logo-creator": {
    en: {
      intro:
        "Build a simple logo from the Lucide icon library. Search for an icon, adjust its color, size, stroke, padding and background, then export it as SVG or PNG.",
      steps: [
        "Search the icon library and click an icon to select it.",
        "Customize the icon color, size, stroke width, rotation and padding.",
        "Choose a transparent or solid background color.",
        "Export as SVG, copy the SVG code, or download a PNG in one of the size presets.",
      ],
      faq: [
        {
          q: "Can I use the logos commercially?",
          a: "The Lucide icons are open source under the ISC license, which allows commercial use. Check the Lucide license if you need the exact terms.",
        },
        {
          q: "Should I export SVG or PNG?",
          a: "Use SVG for websites and print because it scales to any size without losing quality. Use PNG when a platform only accepts raster images.",
        },
        {
          q: "Is this a full logo design tool?",
          a: "No. It is a fast way to make icon-based marks. For a complete brand identity with custom lettering you will want a design tool or a designer.",
        },
      ],
    },
    ro: {
      intro:
        "Creează un logo simplu din biblioteca de icoane Lucide. Caută o icoană, ajustează culoarea, dimensiunea, grosimea liniei, spațierea și fundalul, apoi exportă-o ca SVG sau PNG.",
      steps: [
        "Caută în biblioteca de icoane și apasă pe o icoană pentru a o selecta.",
        "Personalizează culoarea, dimensiunea, grosimea liniei, rotirea și spațierea.",
        "Alege un fundal transparent sau o culoare solidă.",
        "Exportă ca SVG, copiază codul SVG sau descarcă un PNG în una dintre dimensiunile presetate.",
      ],
      faq: [
        {
          q: "Pot folosi logo-urile în scop comercial?",
          a: "Icoanele Lucide sunt open source, sub licența ISC, care permite utilizarea comercială. Verifică licența Lucide dacă ai nevoie de termenii exacți.",
        },
        {
          q: "Să exportez SVG sau PNG?",
          a: "Folosește SVG pentru site-uri și tipar, pentru că se scalează la orice dimensiune fără pierderi. Folosește PNG când o platformă acceptă doar imagini raster.",
        },
        {
          q: "Este un instrument complet de design de logo?",
          a: "Nu. Este o metodă rapidă de a crea embleme bazate pe icoane. Pentru o identitate de brand completă, cu litere personalizate, ai nevoie de un instrument de design sau de un designer.",
        },
      ],
    },
  },

  "json-formatter": {
    en: {
      intro:
        "Beautify, minify and validate JSON in one place. Paste your data to get readable, indented output, or compress it to a single line. Invalid JSON is reported with the line and column of the error.",
      steps: [
        "Paste your JSON into the input box.",
        "Click Beautify for indented, readable output, or Minify to remove whitespace.",
        "If the JSON is invalid, check the reported line and column to find the problem.",
        "Copy the formatted result with the copy button.",
      ],
      faq: [
        {
          q: "Is my JSON sent to a server?",
          a: "No. Formatting and validation happen entirely in your browser, so your data stays on your device.",
        },
        {
          q: "What is the difference between beautify and minify?",
          a: "Beautify adds indentation and line breaks to make JSON easy to read. Minify removes all unnecessary whitespace to make the file as small as possible.",
        },
        {
          q: "Why does it say my JSON is invalid?",
          a: "Common causes are trailing commas, single quotes instead of double quotes, unquoted keys and missing brackets. The error position points to where parsing failed.",
        },
      ],
    },
    ro: {
      intro:
        "Formatează, minimizează și validează JSON într-un singur loc. Lipește datele pentru a obține un rezultat indentat și lizibil sau comprimă-le pe o singură linie. JSON-ul invalid este raportat cu linia și coloana erorii.",
      steps: [
        "Lipește JSON-ul în caseta de intrare.",
        "Apasă Formatează pentru un rezultat indentat și lizibil sau Minimizează pentru a elimina spațiile.",
        "Dacă JSON-ul este invalid, verifică linia și coloana raportate pentru a găsi problema.",
        "Copiază rezultatul cu butonul de copiere.",
      ],
      faq: [
        {
          q: "JSON-ul meu este trimis pe un server?",
          a: "Nu. Formatarea și validarea se fac integral în browser, deci datele rămân pe dispozitivul tău.",
        },
        {
          q: "Care este diferența dintre formatare și minimizare?",
          a: "Formatarea adaugă indentare și linii noi pentru a face JSON-ul ușor de citit. Minimizarea elimină spațiile inutile pentru a obține un fișier cât mai mic.",
        },
        {
          q: "De ce spune că JSON-ul meu este invalid?",
          a: "Cauze frecvente: virgule la final, ghilimele simple în loc de duble, chei fără ghilimele și paranteze lipsă. Poziția erorii arată unde a eșuat analiza.",
        },
      ],
    },
  },

  "json-to-toon": {
    en: {
      intro:
        "Convert JSON to TOON, a compact notation that can use noticeably fewer tokens than JSON when you send structured data to a large language model. The tool shows an estimate of how much smaller the result is.",
      steps: [
        "Paste valid JSON into the input box.",
        "Click Convert to TOON.",
        "Check the token reduction estimate shown for your data.",
        "Copy the TOON output and use it in your prompt.",
      ],
      faq: [
        {
          q: "What is TOON?",
          a: "TOON is a compact, readable format for structured data designed to cut the token count of JSON when it is used in LLM prompts, especially for lists of similar objects.",
        },
        {
          q: "How much will I save?",
          a: "It depends on your data. Uniform arrays of objects save the most, and the tool shows an estimated reduction for the JSON you paste. Treat the figure as an approximation.",
        },
        {
          q: "Is my data uploaded?",
          a: "No. The conversion runs in your browser and your JSON is never sent anywhere.",
        },
      ],
    },
    ro: {
      intro:
        "Convertește JSON în TOON, o notație compactă care poate folosi vizibil mai puțini tokeni decât JSON atunci când trimiți date structurate către un model de limbaj. Instrumentul afișează o estimare a cât de mic este rezultatul.",
      steps: [
        "Lipește un JSON valid în caseta de intrare.",
        "Apasă Convertește în TOON.",
        "Verifică estimarea reducerii de tokeni afișată pentru datele tale.",
        "Copiază rezultatul TOON și folosește-l în prompt.",
      ],
      faq: [
        {
          q: "Ce este TOON?",
          a: "TOON este un format compact și lizibil pentru date structurate, creat pentru a reduce numărul de tokeni ai JSON-ului în prompturile LLM, mai ales pentru liste de obiecte similare.",
        },
        {
          q: "Cât economisesc?",
          a: "Depinde de date. Listele uniforme de obiecte economisesc cel mai mult, iar instrumentul afișează o reducere estimată pentru JSON-ul lipit. Consideră cifra o aproximare.",
        },
        {
          q: "Datele mele sunt încărcate?",
          a: "Nu. Conversia rulează în browser și JSON-ul tău nu este trimis nicăieri.",
        },
      ],
    },
  },

  "color-palette-generator": {
    en: {
      intro:
        "Generate color palettes in one click and lock the colors you like while re-rolling the rest. Copy any color as a HEX code and use it in your CSS, design tool or brand guidelines.",
      steps: [
        "Click Generate palette to create a new set of colors.",
        "Lock any color you want to keep.",
        "Generate again to re-roll only the unlocked colors.",
        "Copy the HEX code of any color you want to use.",
      ],
      faq: [
        {
          q: "How do I keep a color I like?",
          a: "Use the lock button on that color. Locked colors stay in place when you generate a new palette.",
        },
        {
          q: "What format are the colors copied in?",
          a: "Colors are copied as HEX codes, which work in CSS, Figma, Sketch and most other design tools. Use the Color Converter if you need RGB or HSL.",
        },
        {
          q: "Are the palettes random?",
          a: "Yes. Each palette is generated randomly in your browser, so keep generating until you find a combination that works.",
        },
      ],
    },
    ro: {
      intro:
        "Generează palete de culori dintr-un click și blochează culorile care îți plac în timp ce le regenerezi pe celelalte. Copiază orice culoare ca cod HEX și folosește-o în CSS, în instrumentul de design sau în ghidul de brand.",
      steps: [
        "Apasă Generează paleta pentru a crea un set nou de culori.",
        "Blochează orice culoare vrei să o păstrezi.",
        "Generează din nou pentru a regenera doar culorile deblocate.",
        "Copiază codul HEX al culorii pe care vrei să o folosești.",
      ],
      faq: [
        {
          q: "Cum păstrez o culoare care îmi place?",
          a: "Folosește butonul de blocare de pe acea culoare. Culorile blocate rămân pe loc când generezi o paletă nouă.",
        },
        {
          q: "În ce format se copiază culorile?",
          a: "Culorile se copiază ca coduri HEX, care funcționează în CSS, Figma, Sketch și majoritatea instrumentelor de design. Folosește Convertorul de culori dacă ai nevoie de RGB sau HSL.",
        },
        {
          q: "Paletele sunt aleatorii?",
          a: "Da. Fiecare paletă este generată aleatoriu în browser, așa că poți genera până găsești o combinație potrivită.",
        },
      ],
    },
  },

  "sitemap-generator": {
    en: {
      intro:
        "Create an XML sitemap for your website by crawling it automatically or by adding URLs by hand. Choose whether to include last-modified dates, change frequency and priority, then copy or download the file.",
      steps: [
        "Enter your website URL and click Crawl to discover its pages, or add URLs manually.",
        "Review the list and remove any pages you do not want indexed.",
        "Choose which optional fields to include: lastmod, changefreq and priority.",
        "Copy or download the generated sitemap.xml and upload it to your site's root.",
      ],
      faq: [
        {
          q: "Where do I put the sitemap file?",
          a: "Upload sitemap.xml to the root of your website and reference it in your robots.txt file. You can also submit it in Google Search Console and Bing Webmaster Tools.",
        },
        {
          q: "Is there a limit to how many pages the crawler finds?",
          a: "Yes. The crawler checks a limited number of pages per run, so for large sites add the remaining URLs manually or use a sitemap plugin for your CMS.",
        },
        {
          q: "Do priority and changefreq matter?",
          a: "Search engines treat them as hints and often ignore them. An accurate list of URLs and correct lastmod dates matter more.",
        },
      ],
    },
    ro: {
      intro:
        "Creează un sitemap XML pentru site-ul tău, parcurgându-l automat sau adăugând manual URL-uri. Alege dacă incluzi data ultimei modificări, frecvența de modificare și prioritatea, apoi copiază sau descarcă fișierul.",
      steps: [
        "Introdu adresa site-ului și apasă Scanează pentru a descoperi paginile, sau adaugă URL-uri manual.",
        "Verifică lista și elimină paginile pe care nu vrei să le indexezi.",
        "Alege câmpurile opționale: lastmod, changefreq și priority.",
        "Copiază sau descarcă sitemap.xml generat și încarcă-l în rădăcina site-ului.",
      ],
      faq: [
        {
          q: "Unde pun fișierul sitemap?",
          a: "Încarcă sitemap.xml în rădăcina site-ului și menționează-l în fișierul robots.txt. Îl poți trimite și în Google Search Console și Bing Webmaster Tools.",
        },
        {
          q: "Există o limită de pagini descoperite de crawler?",
          a: "Da. Crawlerul verifică un număr limitat de pagini la o rulare, deci pentru site-uri mari adaugă manual restul URL-urilor sau folosește un plugin de sitemap pentru CMS.",
        },
        {
          q: "Contează priority și changefreq?",
          a: "Motoarele de căutare le tratează ca indicii și adesea le ignoră. O listă corectă de URL-uri și date lastmod exacte contează mai mult.",
        },
      ],
    },
  },

  "robots-txt-generator": {
    en: {
      intro:
        "Build a robots.txt file that tells search engine crawlers which parts of your site they may visit. Pick a user agent, add allow and disallow paths, set an optional crawl delay and link your sitemap.",
      steps: [
        "Choose a user agent: all bots, a specific search engine, or a custom one.",
        "Add the paths to allow and the paths to disallow.",
        "Optionally set a crawl delay and add your sitemap URL.",
        "Copy or download the file and place it at the root of your domain as /robots.txt.",
      ],
      faq: [
        {
          q: "Does robots.txt hide pages from Google?",
          a: "Not reliably. It asks well-behaved crawlers not to fetch a page, but a blocked URL can still appear in search results if other sites link to it. Use a noindex tag or authentication to keep pages out.",
        },
        {
          q: "Where must robots.txt be located?",
          a: "At the root of the host, for example https://example.com/robots.txt. Crawlers do not look for it in subfolders.",
        },
        {
          q: "Do all search engines respect crawl-delay?",
          a: "No. Bing and some others honor it, but Google ignores the crawl-delay directive.",
        },
      ],
    },
    ro: {
      intro:
        "Construiește un fișier robots.txt care spune crawlerelor motoarelor de căutare ce părți ale site-ului pot vizita. Alege un user agent, adaugă căi permise și interzise, setează opțional o întârziere între cereri și leagă sitemap-ul.",
      steps: [
        "Alege un user agent: toți roboții, un motor de căutare anume sau unul personalizat.",
        "Adaugă căile permise și căile interzise.",
        "Opțional, setează o întârziere între cereri și adaugă URL-ul sitemap-ului.",
        "Copiază sau descarcă fișierul și pune-l în rădăcina domeniului ca /robots.txt.",
      ],
      faq: [
        {
          q: "Robots.txt ascunde paginile de Google?",
          a: "Nu în mod sigur. Cere crawlerelor corecte să nu acceseze o pagină, dar un URL blocat poate apărea în rezultate dacă alte site-uri trimit către el. Folosește eticheta noindex sau autentificare pentru a ține paginile departe.",
        },
        {
          q: "Unde trebuie să fie robots.txt?",
          a: "În rădăcina gazdei, de exemplu https://example.com/robots.txt. Crawlerele nu îl caută în subfoldere.",
        },
        {
          q: "Toate motoarele de căutare respectă crawl-delay?",
          a: "Nu. Bing și altele îl respectă, dar Google ignoră directiva crawl-delay.",
        },
      ],
    },
  },

  "redirects-tester": {
    en: {
      intro:
        "Check how your website responds to the common URL variations: HTTP and HTTPS, with and without www, with and without a trailing slash, and with index.html. See the status codes and the final URL for each request.",
      steps: [
        "Enter a domain or a full URL.",
        "Click Run tests to request each URL variation.",
        "Review the status code, redirect chain and final URL for each test.",
        "Use Copy curl to reproduce a request in your terminal.",
      ],
      faq: [
        {
          q: "Why test redirects?",
          a: "Duplicate versions of the same page (http vs https, www vs non-www) split SEO signals. They should all redirect to one canonical URL, ideally with a 301 status.",
        },
        {
          q: "What is the difference between a 301 and a 302?",
          a: "A 301 is a permanent redirect and tells search engines to move ranking signals to the new URL. A 302 is temporary and keeps the original URL in the index.",
        },
        {
          q: "Why do some results say they are blocked by CORS?",
          a: "Some servers refuse requests made from a browser. In that case use the Open or Copy curl buttons to check the URL directly.",
        },
      ],
    },
    ro: {
      intro:
        "Verifică cum răspunde site-ul tău la variațiile obișnuite de URL: HTTP și HTTPS, cu și fără www, cu și fără slash la final și cu index.html. Vezi codurile de stare și URL-ul final pentru fiecare cerere.",
      steps: [
        "Introdu un domeniu sau un URL complet.",
        "Apasă Rulează testele pentru a solicita fiecare variație de URL.",
        "Verifică codul de stare, lanțul de redirecționări și URL-ul final pentru fiecare test.",
        "Folosește Copiază curl pentru a reproduce o cerere în terminal.",
      ],
      faq: [
        {
          q: "De ce testez redirecționările?",
          a: "Versiunile duplicate ale aceleiași pagini (http vs https, www vs fără www) împart semnalele SEO. Toate ar trebui să redirecționeze către un singur URL canonic, ideal cu statusul 301.",
        },
        {
          q: "Care este diferența dintre 301 și 302?",
          a: "301 este o redirecționare permanentă și îi spune motorului de căutare să mute semnalele de clasare pe noul URL. 302 este temporară și păstrează URL-ul original în index.",
        },
        {
          q: "De ce unele rezultate spun că sunt blocate de CORS?",
          a: "Unele servere refuză cererile făcute dintr-un browser. În acest caz folosește butoanele Deschide sau Copiază curl pentru a verifica direct URL-ul.",
        },
      ],
    },
  },

  "compound-calculator": {
    en: {
      intro:
        "Calculate how an investment grows with compound interest. Enter the starting amount, annual rate, number of years and compounding frequency to see the final amount, total interest earned and a year-by-year breakdown.",
      steps: [
        "Enter the initial investment amount.",
        "Enter the annual interest rate and the investment period in years.",
        "Choose how often interest compounds: annually, semi-annually, quarterly or monthly.",
        "Read the final amount, total interest and the year-by-year table.",
      ],
      faq: [
        {
          q: "What is compound interest?",
          a: "Compound interest is interest earned on both your original money and on the interest already added. Over time this makes balances grow faster than with simple interest.",
        },
        {
          q: "How does compounding frequency change the result?",
          a: "The more often interest is added, the more interest you earn on interest. Monthly compounding gives a slightly higher result than annual compounding at the same rate.",
        },
        {
          q: "Does the calculator include regular contributions, taxes or fees?",
          a: "No. It models a single starting amount at a fixed rate. Real returns also depend on contributions, fees, taxes and inflation. This is not financial advice.",
        },
      ],
    },
    ro: {
      intro:
        "Calculează cum crește o investiție cu dobândă compusă. Introdu suma inițială, rata anuală, numărul de ani și frecvența de capitalizare pentru a vedea suma finală, dobânda totală câștigată și un tabel an cu an.",
      steps: [
        "Introdu suma investiției inițiale.",
        "Introdu rata anuală a dobânzii și perioada investiției, în ani.",
        "Alege cât de des se capitalizează dobânda: anual, semestrial, trimestrial sau lunar.",
        "Citește suma finală, dobânda totală și tabelul an cu an.",
      ],
      faq: [
        {
          q: "Ce este dobânda compusă?",
          a: "Dobânda compusă este dobânda câștigată atât la banii inițiali, cât și la dobânda deja adăugată. În timp, soldurile cresc mai repede decât cu dobânda simplă.",
        },
        {
          q: "Cum schimbă frecvența de capitalizare rezultatul?",
          a: "Cu cât dobânda se adaugă mai des, cu atât câștigi mai multă dobândă la dobândă. Capitalizarea lunară dă un rezultat puțin mai mare decât cea anuală, la aceeași rată.",
        },
        {
          q: "Calculatorul include contribuții regulate, taxe sau comisioane?",
          a: "Nu. Modelează o singură sumă inițială, la o rată fixă. Randamentele reale depind și de contribuții, comisioane, taxe și inflație. Nu reprezintă sfat financiar.",
        },
      ],
    },
  },

  "color-converter": {
    en: {
      intro:
        "Convert colors between HEX, RGB and HSL and see a live preview. Edit any one format and the other two update automatically, ready to copy into your CSS or design tool.",
      steps: [
        "Enter a color in HEX, RGB or HSL.",
        "Check the color preview to confirm it looks right.",
        "Read the converted values in the other two formats.",
        "Click the copy button next to the format you need.",
      ],
      faq: [
        {
          q: "What is the difference between HEX, RGB and HSL?",
          a: "HEX and RGB both describe a color by its red, green and blue amounts. HSL describes it by hue, saturation and lightness, which is often easier to adjust by hand.",
        },
        {
          q: "Which format should I use in CSS?",
          a: "All three work in CSS. HEX is the most common and compact, while HSL is convenient for creating lighter or darker variants of a color.",
        },
        {
          q: "Does it support 3-digit HEX codes?",
          a: "Yes. Short HEX codes such as #0af are accepted and treated as shorthand for #00aaff.",
        },
      ],
    },
    ro: {
      intro:
        "Convertește culorile între HEX, RGB și HSL și vezi o previzualizare în timp real. Modifici un format, iar celelalte două se actualizează automat, gata de copiat în CSS sau în instrumentul de design.",
      steps: [
        "Introdu o culoare în HEX, RGB sau HSL.",
        "Verifică previzualizarea pentru a confirma că arată corect.",
        "Citește valorile convertite în celelalte două formate.",
        "Apasă butonul de copiere de lângă formatul de care ai nevoie.",
      ],
      faq: [
        {
          q: "Care este diferența dintre HEX, RGB și HSL?",
          a: "HEX și RGB descriu o culoare prin cantitățile de roșu, verde și albastru. HSL o descrie prin nuanță, saturație și luminozitate, ceea ce este adesea mai ușor de ajustat manual.",
        },
        {
          q: "Ce format să folosesc în CSS?",
          a: "Toate trei funcționează în CSS. HEX este cel mai comun și compact, iar HSL este comod pentru a crea variante mai deschise sau mai închise ale unei culori.",
        },
        {
          q: "Acceptă coduri HEX de 3 cifre?",
          a: "Da. Codurile HEX scurte, precum #0af, sunt acceptate și tratate ca prescurtări pentru #00aaff.",
        },
      ],
    },
  },

  "color-inverter": {
    en: {
      intro:
        "Invert any color to get its exact opposite. Enter a HEX color to see the original and the inverted color side by side, with HEX and RGB values you can copy.",
      steps: [
        "Enter a HEX color such as #22D3EE.",
        "Compare the original and inverted colors in the preview.",
        "Read the inverted value in HEX and RGB.",
        "Copy the format you need.",
      ],
      faq: [
        {
          q: "How is a color inverted?",
          a: "Each red, green and blue channel is subtracted from 255. For example, white (#FFFFFF) becomes black (#000000).",
        },
        {
          q: "Is the inverted color the same as the complementary color?",
          a: "Not exactly. Inversion flips each RGB channel, while a complementary color sits opposite on the color wheel by hue. They can look different.",
        },
        {
          q: "Will an inverted color have good contrast?",
          a: "Often, but not always. Mid-tone colors such as gray invert to a similar color, so check text contrast separately.",
        },
      ],
    },
    ro: {
      intro:
        "Inversează orice culoare pentru a obține opusul ei exact. Introdu o culoare HEX pentru a vedea originalul și culoarea inversată alături, cu valori HEX și RGB pe care le poți copia.",
      steps: [
        "Introdu o culoare HEX, de exemplu #22D3EE.",
        "Compară culoarea originală și cea inversată în previzualizare.",
        "Citește valoarea inversată în HEX și RGB.",
        "Copiază formatul de care ai nevoie.",
      ],
      faq: [
        {
          q: "Cum se inversează o culoare?",
          a: "Fiecare canal de roșu, verde și albastru se scade din 255. De exemplu, albul (#FFFFFF) devine negru (#000000).",
        },
        {
          q: "Culoarea inversată este aceeași cu cea complementară?",
          a: "Nu tocmai. Inversarea răstoarnă fiecare canal RGB, în timp ce o culoare complementară se află opusă pe roata culorilor, ca nuanță. Pot arăta diferit.",
        },
        {
          q: "O culoare inversată are contrast bun?",
          a: "Adesea da, dar nu mereu. Culorile din zona de mijloc, precum griul, se inversează într-o culoare asemănătoare, așa că verifică separat contrastul textului.",
        },
      ],
    },
  },

  "regex-tester": {
    en: {
      intro:
        "Test regular expressions against your own text with real-time results. See every match with its position, plus capture groups and named groups, and switch flags on and off.",
      steps: [
        "Enter your regular expression pattern.",
        "Select the flags you need, such as global, ignore case or multiline.",
        "Paste or type the text to test against.",
        "Review the matches, positions and capture groups shown below.",
      ],
      faq: [
        {
          q: "Which regex flavor does this use?",
          a: "It uses the JavaScript regular expression engine built into your browser, so patterns behave the same way they do in JavaScript code.",
        },
        {
          q: "What do the flags mean?",
          a: "Global finds all matches, ignore case ignores letter case, multiline lets ^ and $ match line boundaries, dot all lets . match newlines, unicode enables full Unicode handling and sticky matches only from the last index.",
        },
        {
          q: "Why do I see an invalid regex message?",
          a: "The pattern has a syntax error, such as an unclosed bracket or group. Fix the pattern and the results update immediately.",
        },
      ],
    },
    ro: {
      intro:
        "Testează expresii regulate pe propriul text, cu rezultate în timp real. Vezi fiecare potrivire cu poziția ei, plus grupurile de captură și grupurile cu nume, și activează sau dezactivează flagurile.",
      steps: [
        "Introdu expresia regulată.",
        "Selectează flagurile de care ai nevoie, precum global, ignoră majusculele sau multiline.",
        "Lipește sau scrie textul pe care îl testezi.",
        "Verifică potrivirile, pozițiile și grupurile de captură afișate dedesubt.",
      ],
      faq: [
        {
          q: "Ce variantă de regex folosește?",
          a: "Folosește motorul de expresii regulate JavaScript din browser, deci tiparele se comportă la fel ca în codul JavaScript.",
        },
        {
          q: "Ce înseamnă flagurile?",
          a: "Global găsește toate potrivirile, ignoră majusculele ignoră diferența dintre litere, multiline face ca ^ și $ să potrivească limitele de linie, dot all face ca . să potrivească și linii noi, unicode activează tratarea completă Unicode, iar sticky potrivește doar de la ultimul index.",
        },
        {
          q: "De ce văd mesajul de regex invalid?",
          a: "Tiparul are o eroare de sintaxă, de exemplu o paranteză sau un grup neînchis. Corectează tiparul și rezultatele se actualizează imediat.",
        },
      ],
    },
  },

  "time-zone-converter": {
    en: {
      intro:
        "Convert a date and time across several time zones at once. Add the cities or zones you care about, set a source time or use the current time, and compare them side by side. Handy for scheduling meetings across countries.",
      steps: [
        "Set the source time, or click Use Current Time.",
        "Search for and add the time zones you want to compare.",
        "Read the converted time for each zone.",
        "Copy a result, or remove zones you no longer need.",
      ],
      faq: [
        {
          q: "Does it handle daylight saving time?",
          a: "Yes. Conversions use your browser's built-in time zone data, which accounts for daylight saving rules in each zone.",
        },
        {
          q: "Will my selected time zones be remembered?",
          a: "Yes. Your list is saved in your browser's local storage on your own device, so it is there next time you visit.",
        },
        {
          q: "Which time zones are available?",
          a: "Every IANA time zone supported by your browser, searchable by name, with popular zones listed for quick access.",
        },
      ],
    },
    ro: {
      intro:
        "Convertește o dată și o oră în mai multe fusuri orare deodată. Adaugă orașele sau fusurile care te interesează, setează o oră sursă sau folosește ora curentă și compară-le alături. Util pentru programarea întâlnirilor între țări.",
      steps: [
        "Setează ora sursă sau apasă Folosește ora curentă.",
        "Caută și adaugă fusurile orare pe care vrei să le compari.",
        "Citește ora convertită pentru fiecare fus.",
        "Copiază un rezultat sau elimină fusurile de care nu mai ai nevoie.",
      ],
      faq: [
        {
          q: "Ține cont de ora de vară?",
          a: "Da. Conversiile folosesc datele de fus orar integrate în browser, care includ regulile orei de vară din fiecare fus.",
        },
        {
          q: "Fusurile alese sunt memorate?",
          a: "Da. Lista ta este salvată în stocarea locală a browserului, pe dispozitivul tău, deci o regăsești la următoarea vizită.",
        },
        {
          q: "Ce fusuri orare sunt disponibile?",
          a: "Toate fusurile orare IANA acceptate de browser, căutabile după nume, cu fusurile populare afișate pentru acces rapid.",
        },
      ],
    },
  },

  "word-counter": {
    en: {
      intro:
        "Count words, characters, sentences and paragraphs as you type, and get an estimated reading time. Useful for essays, social posts, meta descriptions and any text with a length limit.",
      steps: [
        "Type or paste your text into the box.",
        "Watch the counts for words, characters and sentences update live.",
        "Check the reading time estimate.",
        "Click Copy Stats to copy the numbers, or Clear to start over.",
      ],
      faq: [
        {
          q: "Does it count characters with and without spaces?",
          a: "Yes. It shows both the total character count and the count without spaces, which is handy for limits that ignore spaces.",
        },
        {
          q: "How is reading time estimated?",
          a: "It divides the word count by an average adult reading speed. Treat it as a rough guide, since real reading speed varies from person to person.",
        },
        {
          q: "Is my text stored or sent anywhere?",
          a: "No. Counting happens in your browser and your text is never uploaded.",
        },
      ],
    },
    ro: {
      intro:
        "Numără cuvintele, caracterele, propozițiile și paragrafele în timp ce scrii și primește o estimare a timpului de citire. Util pentru eseuri, postări, meta descrieri și orice text cu limită de lungime.",
      steps: [
        "Scrie sau lipește textul în casetă.",
        "Urmărește cum se actualizează în timp real numărul de cuvinte, caractere și propoziții.",
        "Verifică estimarea timpului de citire.",
        "Apasă Copiază statisticile pentru a copia cifrele sau Șterge pentru a o lua de la capăt.",
      ],
      faq: [
        {
          q: "Numără caracterele cu și fără spații?",
          a: "Da. Afișează atât numărul total de caractere, cât și numărul fără spații, util pentru limitele care ignoră spațiile.",
        },
        {
          q: "Cum este estimat timpul de citire?",
          a: "Numărul de cuvinte este împărțit la o viteză medie de citire a unui adult. Consideră-l un reper aproximativ, deoarece viteza reală diferă de la om la om.",
        },
        {
          q: "Textul meu este salvat sau trimis undeva?",
          a: "Nu. Numărătoarea se face în browser, iar textul tău nu este încărcat.",
        },
      ],
    },
  },

  "lorem-ipsum-generator": {
    en: {
      intro:
        "Generate Lorem Ipsum placeholder text for mockups, wireframes and layouts. Choose paragraphs, sentences or words, set the quantity, and copy the result.",
      steps: [
        "Choose the output type: paragraphs, sentences or words.",
        "Set how many you need.",
        "Choose whether the text should start with the classic \"Lorem ipsum dolor sit amet\".",
        "Click Generate, then copy the text.",
      ],
      faq: [
        {
          q: "What is Lorem Ipsum?",
          a: "Lorem Ipsum is dummy text used in design and publishing to show how a layout looks with realistic text before the real content is ready.",
        },
        {
          q: "Does the text mean anything?",
          a: "No. It is scrambled Latin-like text, which keeps readers focused on the design instead of the content.",
        },
        {
          q: "Can I use it in commercial projects?",
          a: "Yes. Placeholder text has no copyright restrictions. Replace it with real content before publishing.",
        },
      ],
    },
    ro: {
      intro:
        "Generează text substituent Lorem Ipsum pentru machete, wireframe-uri și layout-uri. Alege paragrafe, propoziții sau cuvinte, setează cantitatea și copiază rezultatul.",
      steps: [
        "Alege tipul de ieșire: paragrafe, propoziții sau cuvinte.",
        "Setează câte ai nevoie.",
        "Alege dacă textul începe cu clasicul „Lorem ipsum dolor sit amet”.",
        "Apasă Generează, apoi copiază textul.",
      ],
      faq: [
        {
          q: "Ce este Lorem Ipsum?",
          a: "Lorem Ipsum este un text fictiv folosit în design și editare pentru a arăta cum arată un layout cu text realist înainte ca textul final să fie gata.",
        },
        {
          q: "Textul înseamnă ceva?",
          a: "Nu. Este un text amestecat, asemănător latinei, care ține atenția pe design, nu pe conținut.",
        },
        {
          q: "Îl pot folosi în proiecte comerciale?",
          a: "Da. Textul substituent nu are restricții de drepturi de autor. Înlocuiește-l cu conținut real înainte de publicare.",
        },
      ],
    },
  },

  "base64-encoder": {
    en: {
      intro:
        "Encode text to Base64 or decode Base64 back to text. The tool handles UTF-8 characters correctly, and a swap button turns the output into the next input.",
      steps: [
        "Choose Encode or Decode.",
        "Paste your text or Base64 string into the input box.",
        "Read the result in the output box.",
        "Copy the result, or use Swap to feed it back in the other direction.",
      ],
      faq: [
        {
          q: "What is Base64 used for?",
          a: "Base64 represents binary or special-character data using plain text characters, so it can travel safely in URLs, JSON, emails and data URIs.",
        },
        {
          q: "Is Base64 encryption?",
          a: "No. It is only an encoding that anyone can reverse, so never use it to protect secrets.",
        },
        {
          q: "Why does decoding say my input is invalid?",
          a: "The input is not valid Base64. Check for missing padding characters, extra symbols or text that was truncated when copied.",
        },
      ],
    },
    ro: {
      intro:
        "Codifică text în Base64 sau decodifică Base64 înapoi în text. Instrumentul tratează corect caracterele UTF-8, iar butonul de inversare transformă rezultatul în următoarea intrare.",
      steps: [
        "Alege Codifică sau Decodifică.",
        "Lipește textul sau șirul Base64 în caseta de intrare.",
        "Citește rezultatul în caseta de ieșire.",
        "Copiază rezultatul sau folosește Inversează ca să-l trimiți înapoi în sens opus.",
      ],
      faq: [
        {
          q: "La ce se folosește Base64?",
          a: "Base64 reprezintă date binare sau cu caractere speciale folosind caractere text simple, deci poate circula sigur în URL-uri, JSON, e-mailuri și data URI-uri.",
        },
        {
          q: "Base64 este criptare?",
          a: "Nu. Este doar o codificare pe care oricine o poate inversa, deci nu o folosi niciodată pentru a proteja secrete.",
        },
        {
          q: "De ce spune la decodificare că intrarea este invalidă?",
          a: "Intrarea nu este Base64 valid. Verifică dacă lipsesc caracterele de completare, dacă există simboluri în plus sau dacă textul a fost trunchiat la copiere.",
        },
      ],
    },
  },

  "uuid-generator": {
    en: {
      intro:
        "Generate random version 4 UUIDs (GUIDs) for database keys, API tokens, test data and more. Create up to 100 at once and choose lowercase, uppercase or braces.",
      steps: [
        "Enter how many UUIDs you need, from 1 to 100.",
        "Choose the format: lowercase, uppercase or wrapped in braces.",
        "Click Generate.",
        "Copy all the UUIDs at once.",
      ],
      faq: [
        {
          q: "What is a UUID?",
          a: "A UUID, also called a GUID, is a 128-bit identifier written as 32 hexadecimal digits in five groups, such as 123e4567-e89b-42d3-a456-426614174000.",
        },
        {
          q: "Can two UUIDs ever collide?",
          a: "Random version 4 UUIDs are generated with 122 random bits, so the chance of two matching is so small that they are treated as unique in practice.",
        },
        {
          q: "Is it safe to use UUIDs as secrets?",
          a: "They are unpredictable, but they are meant as identifiers. For passwords or secret keys use a dedicated generator such as the Password Generator.",
        },
      ],
    },
    ro: {
      intro:
        "Generează UUID-uri (GUID-uri) aleatorii de versiunea 4 pentru chei de baze de date, tokeni API, date de test și altele. Creează până la 100 odată și alege litere mici, majuscule sau acolade.",
      steps: [
        "Introdu câte UUID-uri ai nevoie, de la 1 la 100.",
        "Alege formatul: litere mici, majuscule sau încadrate în acolade.",
        "Apasă Generează.",
        "Copiază toate UUID-urile odată.",
      ],
      faq: [
        {
          q: "Ce este un UUID?",
          a: "Un UUID, numit și GUID, este un identificator de 128 de biți scris ca 32 de cifre hexazecimale în cinci grupuri, de exemplu 123e4567-e89b-42d3-a456-426614174000.",
        },
        {
          q: "Se pot ciocni două UUID-uri?",
          a: "UUID-urile aleatorii de versiunea 4 sunt generate cu 122 de biți aleatorii, deci șansa ca două să coincidă este atât de mică încât sunt tratate practic ca unice.",
        },
        {
          q: "Este sigur să folosesc UUID-uri ca secrete?",
          a: "Sunt imprevizibile, dar sunt gândite ca identificatori. Pentru parole sau chei secrete folosește un generator dedicat, precum Generatorul de parole.",
        },
      ],
    },
  },

  "hexagon-drag-and-drop": {
    en: {
      intro:
        "A small interactive board where you place solid hexagons on a hex grid. Click to place or remove hexes, drag to pan, and zoom with the scroll wheel or trackpad. Your board is saved automatically in your browser.",
      steps: [
        "Click an empty slot to place a hexagon, or click a filled slot to remove it.",
        "Drag the board to pan around.",
        "Use the scroll wheel or trackpad to zoom in and out.",
        "Press Reset to clear the board and start again.",
      ],
      faq: [
        {
          q: "Is my board saved?",
          a: "Yes. It is saved automatically in your browser's local storage, so it is still there when you come back on the same device.",
        },
        {
          q: "How do I start over?",
          a: "Press the Reset button to clear all placed hexagons.",
        },
        {
          q: "Does it work on touch screens?",
          a: "Yes. You can tap to place hexagons and drag to pan on most touch devices.",
        },
      ],
    },
    ro: {
      intro:
        "O tablă interactivă mică în care plasezi hexagoane pline pe o grilă hexagonală. Apasă pentru a plasa sau elimina hexagoane, trage pentru a derula și mărește cu rotița sau cu trackpadul. Tabla ta se salvează automat în browser.",
      steps: [
        "Apasă pe un loc gol pentru a plasa un hexagon sau pe unul ocupat pentru a-l elimina.",
        "Trage tabla pentru a o deplasa.",
        "Folosește rotița sau trackpadul pentru a mări și micșora.",
        "Apasă Resetează pentru a goli tabla și a începe din nou.",
      ],
      faq: [
        {
          q: "Tabla mea este salvată?",
          a: "Da. Se salvează automat în stocarea locală a browserului, deci o găsești când revii de pe același dispozitiv.",
        },
        {
          q: "Cum o iau de la capăt?",
          a: "Apasă butonul Resetează pentru a șterge toate hexagoanele plasate.",
        },
        {
          q: "Funcționează pe ecrane tactile?",
          a: "Da. Poți atinge pentru a plasa hexagoane și trage pentru a deplasa tabla pe majoritatea dispozitivelor tactile.",
        },
      ],
    },
  },

  "text-case-converter": {
    en: {
      intro:
        "Convert text between 11 letter cases at once: UPPERCASE, lowercase, Title Case, Sentence case, camelCase, PascalCase, snake_case, kebab-case, CONSTANT_CASE, aLtErNaTiNg and iNVERSE. Paste your text once and copy whichever result you need.",
      steps: [
        "Type or paste your text into the input box.",
        "All 11 conversions appear instantly below.",
        "Find the case you want in the results.",
        "Click Copy next to it to copy the converted text.",
      ],
      faq: [
        {
          q: "What is the difference between camelCase and PascalCase?",
          a: "Both join words without spaces and capitalize each word. camelCase starts with a lowercase letter (myVariableName), PascalCase starts with an uppercase one (MyVariableName).",
        },
        {
          q: "When should I use snake_case or kebab-case?",
          a: "snake_case is common for variable names in Python and database columns, while kebab-case is common for URLs, CSS classes and file names.",
        },
        {
          q: "Does it handle accents and diacritics?",
          a: "Yes. Letters such as ă, â, î, ș and ț are converted correctly, and existing camelCase text is split into words when you convert to another case.",
        },
      ],
    },
    ro: {
      intro:
        "Convertește textul între 11 stiluri de scriere deodată: MAJUSCULE, minuscule, Title Case, propoziție, camelCase, PascalCase, snake_case, kebab-case, CONSTANT_CASE, alternant și inversat. Lipește textul o singură dată și copiază rezultatul de care ai nevoie.",
      steps: [
        "Scrie sau lipește textul în caseta de intrare.",
        "Toate cele 11 conversii apar instant dedesubt.",
        "Găsește stilul dorit în rezultate.",
        "Apasă Copiază lângă el pentru a copia textul convertit.",
      ],
      faq: [
        {
          q: "Care este diferența dintre camelCase și PascalCase?",
          a: "Ambele unesc cuvintele fără spații și pun majusculă fiecărui cuvânt. camelCase începe cu literă mică (myVariableName), PascalCase începe cu majusculă (MyVariableName).",
        },
        {
          q: "Când folosesc snake_case sau kebab-case?",
          a: "snake_case este frecvent pentru nume de variabile în Python și coloane de baze de date, iar kebab-case pentru URL-uri, clase CSS și nume de fișiere.",
        },
        {
          q: "Tratează diacriticele?",
          a: "Da. Literele precum ă, â, î, ș și ț sunt convertite corect, iar textul camelCase existent este împărțit în cuvinte când convertești în alt stil.",
        },
      ],
    },
  },

  "image-to-pdf": {
    en: {
      intro:
        "Combine one or many images into a single PDF without uploading them anywhere. Add JPG, PNG, WebP or GIF files, reorder the pages, choose the page size and margin, and download the PDF.",
      steps: [
        "Click the drop area or drag your images onto it.",
        "Reorder the images with the arrows, or remove any you do not need.",
        "Choose the page size (A4, Letter or fit to image) and the margin.",
        "Click Create PDF to download the file.",
      ],
      faq: [
        {
          q: "Are my images uploaded to a server?",
          a: "No. The PDF is built entirely in your browser, so your images never leave your device.",
        },
        {
          q: "Which image formats are supported?",
          a: "Any image your browser can open, including JPG, PNG, WebP and GIF.",
        },
        {
          q: "What happens to transparent PNGs?",
          a: "Images are placed on a white background, so transparent areas appear white in the PDF.",
        },
      ],
    },
    ro: {
      intro:
        "Combină una sau mai multe imagini într-un singur PDF, fără să le încarci nicăieri. Adaugă fișiere JPG, PNG, WebP sau GIF, reordonează paginile, alege dimensiunea paginii și marginea, apoi descarcă PDF-ul.",
      steps: [
        "Apasă pe zona de încărcare sau trage imaginile peste ea.",
        "Reordonează imaginile cu săgețile sau elimină-le pe cele de care nu ai nevoie.",
        "Alege dimensiunea paginii (A4, Letter sau potrivită imaginii) și marginea.",
        "Apasă Creează PDF pentru a descărca fișierul.",
      ],
      faq: [
        {
          q: "Imaginile mele sunt încărcate pe un server?",
          a: "Nu. PDF-ul este construit integral în browser, deci imaginile nu părăsesc dispozitivul tău.",
        },
        {
          q: "Ce formate de imagine sunt acceptate?",
          a: "Orice imagine pe care o poate deschide browserul, inclusiv JPG, PNG, WebP și GIF.",
        },
        {
          q: "Ce se întâmplă cu PNG-urile transparente?",
          a: "Imaginile sunt plasate pe fundal alb, deci zonele transparente apar albe în PDF.",
        },
      ],
    },
  },
};
